import { GetServerSideProps } from 'next'
import { NextSeo } from 'next-seo'
import { useRouter } from 'next/router'
import {
  SetStateAction,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Container } from 'src/components/container'
import Device from 'src/components/device'
import { EmptyPostView } from 'src/components/emptyPostView'
import { HotelCardList } from 'src/components/hotel/cardList'
import { SearchQueryMobileFilterTriggerButton } from 'src/components/searchQuery/mobileFilterTriggerButton'
import { SplitLarge } from 'src/components/splitContainer/splitLarge'
import { SplitSmall } from 'src/components/splitContainer/splitSmall'
import { SplitSmallStack } from 'src/components/splitContainer/splitSmallStack'
import { WafFriendlyPass } from 'src/types/waf-friendly-pass'
import { fetchWithCache } from 'src/utils/fetchWithCache'
import { Meta } from 'src/utils/meta'
import { wpApiUrl } from 'src/utils/wpApiUrl'

import styles from './styles.module.scss'
import { SkeletonLoader } from 'src/components/skeleton'
import { HotelSearchQuery } from 'src/components/hotelsSearchQuery'
import { isStaging } from 'src/utils/isStaging'
import { sleep } from 'src/utils/sleep'
import dynamic from 'next/dynamic'
import { CarouselServicePickup } from 'src/components/caroucel/servicePickup'
import { useFetchAllPosts } from 'src/hooks/useFetchAllPosts'

const DynamicSplitContainer = dynamic(
  () => import('src/components/splitContainer/splitContainer'),
  { loading: () => <p>Loading...</p> }
)
const DynamicHotelCard = dynamic(() => import('src/components/hotel/card'), {
  suspense: true,
})
const DynamicPagination = dynamic(() => import('src/components/pagination'), {
  suspense: true,
})

type Props = {
  total: {
    posts: string | number | null
    pages: string | number | null
  }
  areaId: number
  featureId: number
  hotelsList: WafFriendlyPass.hotel[]
  hotelAreaList: WafFriendlyPass.taxonomy[]
  hotelFeatureList: WafFriendlyPass.taxonomy[]
  currentPageNumber: number
  limit: number
  currestsWords: ['']
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const currentPageNumber = Number(context?.params?.page)
  const areaId = Number(context?.query?.area)
  const featureId = Number(context?.query?.hotel_feature)
  return {
    props: {
      areaId,
      featureId,
      currentPageNumber,
    },
  }
}

const HotelPage = ({ areaId, featureId, currentPageNumber }: Props) => {
  const router = useRouter()
  const [loadOnce, setLoadOnce] = useState(false)
  const [loading, setLoading] = useState(false)
  const [queryFlag, setQueryFlag] = useState(false)
  const [filterSubmitState, setFilterSubmitState] = useState(false)
  const [toggle, setToggle] = useState(false)
  const [hotels, setHotels] = useState<WafFriendlyPass.hotel[]>([])
  const [hotelAreas, setHotelAreas] = useState<WafFriendlyPass.taxonomy[]>([])
  const [hotelFeatures, setHotelFeatures] = useState<
    WafFriendlyPass.taxonomy[]
  >([])
  const [total, setTotal] = useState<WafFriendlyPass.total>()
  const limit = 12

  const getPostsSet = async () => {
    setLoading(true)
    const fetchUrl = new URL(
      `${wpApiUrl.hotel}?page=${currentPageNumber}&per_page=${limit}&_fields=id,acf,type`
    )
    const urlSearchParams = new URLSearchParams()
    setHotelAreas(
      (await fetchWithCache(
        `${wpApiUrl.taxonomyArea}`
      )) as Props['hotelAreaList']
    )
    setHotelFeatures(
      (await fetchWithCache(
        `${wpApiUrl.taxonomyHotelFeature}`
      )) as Props['hotelFeatureList']
    )
    let hotelsFetch
    if (areaId || featureId) {
      setQueryFlag(true)
      if (areaId) {
        urlSearchParams.append('area', `${areaId}`)
        fetchUrl.searchParams.append('area', `${areaId}`)
        hotelsFetch = await fetch(String(fetchUrl))
      }
      if (featureId) {
        urlSearchParams.append('hotel_feature', `${featureId}`)
        fetchUrl.searchParams.append('hotel_feature', `${featureId}`)
        hotelsFetch = await fetch(String(fetchUrl))
      }
    } else {
      hotelsFetch = await fetch(fetchUrl.toString())
    }
    const headers = hotelsFetch?.headers
    const total = {
      posts: headers?.get('X-WP-Total'),
      pages: headers?.get('X-WP-TotalPages'),
    }
    const hotelsList = (await hotelsFetch?.json()) as Props['hotelsList']
    setTotal(total)
    setHotels(
      isStaging ? hotelsList : hotelsList.filter((item) => item.acf.status)
    )
    setLoading(false)
  }

  useEffect(() => {
    if (router.isReady) {
      if (loadOnce) {
        return
      }
      if (!filterSubmitState) {
        getPostsSet()
        setLoadOnce(true)
      }
    }
  }, [])

  const updateView = () => {
    if (!filterSubmitState) {
      return
    }
    if (loading) {
      sleep(500).then(() => {
        router.reload()
        return sleep(500)
      })
    }
  }

  useEffect(() => {
    router.events.on('routeChangeComplete', updateView)
    return () => {
      router.events.off('routeChangeComplete', updateView)
    }
  }, [filterSubmitState, loading])

  const { allPosts } = useFetchAllPosts(`${wpApiUrl.allPosts}/hotel`)
  const pickupPosts = allPosts?.filter((post: WafFriendlyPass.hotel) => post.acf.pickup)

  return (
    <div>
      <NextSeo
        title={`会員限定宿泊プラン | ${Meta.TITLE}`}
        openGraph={{
          url: `https://pass.workation-fukuoka.jp/hotel/page/1`,
          title: `会員限定宿泊プラン | ${Meta.TITLE}`,
        }}
      />
      <Container>
        {loadOnce && (
          <>
            <div className={styles.hotel}>
              <h1 className={styles.hotelHeading}>
                {!loading && queryFlag && (
                  <span>
                    「
                    <small>
                      {
                        hotelAreas.find((item) => item.id == String(areaId))
                          ?.name
                      }
                    </small>
                    <small>
                      {
                        hotelFeatures.find(
                          (item) => item.id == String(featureId)
                        )?.name
                      }
                    </small>
                    」の
                  </span>
                )}
                <span>
                  <em className={styles.hotelHeadingAppeal}>会員限定</em>
                  宿泊プラン一覧
                </span>
              </h1>
              {currentPageNumber === 1 && (
                <div className={styles.hotelPickup}>
                  <h2 className={styles.hotelSubHeading}>
                    ユーザーから
                    <span>人気</span>の宿泊プラン
                  </h2>
                  <CarouselServicePickup
                    items={pickupPosts}
                  />
                </div>
              )}
              <div className={styles.hotelSplit}>
                <DynamicSplitContainer>
                  <SplitSmall>
                    <Device>
                      {({ isBrowser }) => {
                        if (isBrowser) {
                          return (
                            <SplitSmallStack heading="条件を絞り込む">
                              <HotelSearchQuery
                                pathname={`/hotel/page/1`}
                                areas={hotelAreas}
                                features={hotelFeatures}
                                setState={(state: boolean) => setLoading(state)}
                                setFilterSubmitState={(state: boolean) =>
                                  setFilterSubmitState(state)
                                }
                              />
                            </SplitSmallStack>
                          )
                        } else {
                          return (
                            <SplitSmallStack column>
                              <SearchQueryMobileFilterTriggerButton
                                state={toggle}
                                setState={(state: boolean) => setToggle(state)}
                              />
                              {toggle ? (
                                <HotelSearchQuery
                                  pathname={`/hotel/page/1`}
                                  areas={hotelAreas}
                                  features={hotelFeatures}
                                  setState={(state: boolean) =>
                                    setLoading(state)
                                  }
                                  setFilterSubmitState={(state: boolean) =>
                                    setFilterSubmitState(state)
                                  }
                                />
                              ) : (
                                false
                              )}
                            </SplitSmallStack>
                          )
                        }
                      }}
                    </Device>
                  </SplitSmall>
                  <SplitLarge>
                    {!loading ? (
                      <>
                        {hotels && (
                          <HotelCardList>
                            <Suspense fallback={<SkeletonLoader count={4} />}>
                              {hotels.map((hotel, idx) => {
                                return (
                                  <DynamicHotelCard hotel={hotel} key={idx} />
                                )
                              })}
                            </Suspense>
                          </HotelCardList>
                        )}
                        {!hotels.length && <EmptyPostView />}
                      </>
                    ) : (
                      <SkeletonLoader count={4} />
                    )}
                  </SplitLarge>
                </DynamicSplitContainer>
              </div>

              {Number(total?.posts) > limit && (
                <div className={styles.hotelPagination}>
                  <Suspense fallback={false}>
                    <DynamicPagination
                      total={Number(total?.posts)}
                      pages={Number(total?.pages)}
                      pathname={`/hotel/page/`}
                      currentPageNumber={currentPageNumber}
                      setState={(state: boolean) => setLoading(state)}
                      setFilterSubmitState={(state: boolean) =>
                        setFilterSubmitState(state)
                      }
                    />
                  </Suspense>
                </div>
              )}
            </div>
          </>
        )}
      </Container>
    </div>
  )
}

export default HotelPage
